import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { BatchBulkFulfillmentProcessMap, FulfillService } from '@app/pages/workspace/fulfill/fulfill.service';
import { CarrierTypes } from '@app/services/carriers/carrier.model';
import { CurrencyRate } from '@app/services/currency-rates/currency-rate.model';
import { CurrencyRatesService } from '@app/services/currency-rates/currency-rates.service';
import { Order } from '@app/services/order/order.model';
import { OrderService } from '@app/services/order/order.service';
import { PrintService } from '@app/services/print/print.service';
import { ShopService } from '@app/services/shops/shop.service';
import { DeliveryTransaction } from '@app/sharedModule/models/delivery-transaction.model';
import { ColumnSettings, GroupAction, RowAction, TableFilterType } from '@app/sharedModule/models/table-settings.model';
import { PdqToastComponent } from '@app/storybook/components/toast/toast.component';
import { ToastService } from '@app/storybook/services/toast/toast.service';
import { Filter, QueryParameters, SortField } from '@core/entities/query-parameters.class';
import { HolddialogComponent } from '@pages/workspace/assign/dialogs/holddialog/holddialog.component';
import { MultipleFulfillmentDialog } from '@pages/workspace/fulfill/components/fulfill-side-panel-batch-table/dialog/multiple-fulfillment.dialog';
import { CarrierTypeNames, masterCarrierTypeNames } from '@pdqdev/pdq-shared/dist/CarrierData';
import { TranslatedFulfillmentStatus } from '@pdqdev/pdq-shared/dist/OrderData';
import { ColorCoding, CurrencyUnit, OrderStatus } from '@pdqdev/pdq-shared/dist/entities';
import * as _ from 'lodash';
import moment from 'moment';

import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { isNumber } from 'src/utils/number-utils';
import { PrintDocumentType } from 'src/utils/print-utils';
import {
  AdjacentRowDirection,
  TABLES,
  TimeUnits,
  addCurrencySignToText,
  createStepFilter,
  formatBillTo,
  formatShippingAddressString,
  formatShippingNameString,
  formattedServiceLevel,
  getAdjacentRow,
  getDeliveryPromise,
  getSignOfCurrency,
  modifyDateRangeValuesByColumn,
} from '../../../../../../utils';
import { OrderInBulkFulfillmentProcess, PanelState } from '../../utils/models';
import { columnsConfigs } from './config/config';
import { assignColorCodingToRows } from '@utils/color-coding.utils';
@Component({
  selector: 'fulfill-side-panel-batch-table',
  templateUrl: './fulfill-side-panel-batch-table.component.html',
  styleUrls: ['./fulfill-side-panel-batch-table.component.scss'],
  standalone: false,
})
export class FulfillSidePanelBatchTableComponent implements OnInit, OnDestroy, OnChanges {
  @Input() batch;
  @Input() inWarningOrderIds: string[] = [];
  @Input() bannersHeight: number;
  @Input() filteredOrders: Order[];
  columns: ColumnSettings[] = [...columnsConfigs];
  rowActions = [RowAction.PRINT_ORDER, RowAction.RETURN_ORDER_TO_ASSIGN];
  readonly COLUMN_OPTIONS: ColumnSettings[] = columnsConfigs.filter((column: ColumnSettings) => column.canBeHide);
  groupActions = [GroupAction.FULFILL_ORDERS, GroupAction.PRINT_FULFILL, GroupAction.RETURN_ORDERS];
  readonly tablePrintActions = [
    PrintDocumentType.LABELS,
    PrintDocumentType.PICK_LIST,
    PrintDocumentType.PACKING_SLIPS,
    PrintDocumentType.BROCHURE,
    PrintDocumentType.CARD_ISLE,
  ];
  readonly displayTableFooter: boolean = false;
  readonly footerHeight: number = 0.1;
  readonly TABLES = TABLES;
  orders: Order[] = [];
  backToAssignDays: number;
  disableBackToAssign: boolean;
  selectedOrders: Order[] = [];
  tableFilters = [];
  carrierType: CarrierTypes;
  isMACarrier: boolean = false;
  shopsCurency: CurrencyUnit = 'ILS';
  latestCurrencyRates: CurrencyRate[] = [];
  selectedColumns: string[];
  selectedCarrierOptions: { value: string; text: string }[] = [];
  isPickingMode: boolean = false;
  colorCodingData: ColorCoding[] = [];
  activeTab: string = 'all';
  destroy$ = new Subject();
  constructor(
    private fulfillService: FulfillService,
    private dialog: MatDialog,
    private orderService: OrderService,
    private currencyRatesService: CurrencyRatesService,
    private router: Router,
    private route: ActivatedRoute,
    private _printService: PrintService,
    private shopService: ShopService,
    private toastService: ToastService,
  ) {
    this.loadLocalStorageDefaultColumns();
  }

  async ngOnInit(): Promise<void> {
    this.initCarrierType();
    this.initColorCodingData();
    await this.initCurrencyRatesByDate();
    this.initMetadataConfiguration();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.batch?.currentValue || changes.filteredOrders?.currentValue) {
      if (this.tableFilters.some((filter) => filter.fieldName != 'batchName')) {
        this.getBatchOrders();
      } else {
        this.selectedOrders = [];
        this.processOrders(this.batch, this.filteredOrders?.length ? this.filteredOrders : this.batch.orders);
      }
    }
    if (changes.bannersHeight?.currentValue) {
      this.bannersHeight = changes.bannersHeight.currentValue;
    }
  }

  ngOnDestroy() {
    this.fulfillService.activeTab = 'all';
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  initMetadataConfiguration() {
    this.backToAssignDays = this.fulfillService.shopMetadata?.backToAssignDays ?? 3;
    this.disableBackToAssign = moment(this.batch.createdDate) < moment().add(-1 * this.backToAssignDays, 'day');
    this.shopsCurency = this.fulfillService.shopMetadata?.currency;
    this.isPickingMode = this.fulfillService.shopMetadata?.pickingMode;
    if (this.fulfillService.shopMetadata?.isMultiParcelSupport) {
      const col = this.columns.find((col) => col.name === 'Items');
      if (col) col.name = 'Packages';
    }
    if (this.fulfillService.shopMetadata?.disablePrintLabels) {
      this.rowActions = this.rowActions.filter((action) => action != RowAction.PRINT_ORDER);
    }
  }

  initCarrierType() {
    this.carrierType = this.batch?.selectedCarrier?.type;
    this.isMACarrier = masterCarrierTypeNames.includes(this.carrierType.name);
  }

  async initCurrencyRatesByDate() {
    try {
      const date = this.batch.createdDate.split('T')[0];
      this.latestCurrencyRates = await this.currencyRatesService.getCurrencyRatesByDate(date);
    } catch (e) {
      console.log('Failed to fetch latest currency rates');
    }
  }

  initColorCodingData() {
    this.shopService.shop$.pipe(takeUntil(this.destroy$)).subscribe((shop) => {
      this.colorCodingData = shop.colorCoding;
      (this.orders as any).map((order) => {
        order.currentColorTooltipString =
          !order.colorCoding || order.colorCoding.includes('transparent')
            ? ''
            : order.colorCoding
                .map((colorValue) => {
                  const current = this.colorCodingData?.find((colorData) => colorData.value === colorValue);
                  return current?.label || current?.defaultName || '';
                })
                .join(', ');
      });
    });
  }

  getCurrencyConvertedPrice(price, fromCurrency) {
    // convert only for Master Account carriers
    return this.isMACarrier
      ? this.currencyRatesService.getConvertedPrice(price, fromCurrency, this.shopsCurency, this.latestCurrencyRates)
      : price;
  }

  onReturnSelectedOrdersToAssign(): void {
    this.fulfillService.backToAssignSelectedOrders(this.selectedOrders);
  }

  onPickingSelectedOrders(): void {
    const orderIds: string[] = this.selectedOrders.flatMap((o) =>
      o.fulfillmentStatus != TranslatedFulfillmentStatus.FULFILLED ? o.id : '',
    );
    const dialogRef = this.dialog.open(MultipleFulfillmentDialog, {
      data: {
        orders: this.selectedOrders,
        isPickingMode: this.isPickingMode,
      },
      panelClass: 'multiple-fulfillment-dialog',
      backdropClass: 'global_modal_overlay',
    });
    dialogRef.componentInstance.onPick?.subscribe((results) => {
      this.fulfillService.onLoading.next(true);
      this.fulfillService
        .pickOrders(orderIds)
        .pipe(
          finalize(() => {
            this.fulfillService.onLoading.next(false);
            dialogRef.close();
          }),
        )
        .subscribe(
          () => {},
          (error) => {
            this.toastService.show(PdqToastComponent, {
              mode: 'danger',
              message: error.error,
              duration: TimeUnits._SECOND_IN_MS * 3,
            });
          },
        );

      dialogRef.afterClosed().subscribe(() => {
        this.selectedOrders = [];
      });
    });
  }

  onFulfillSelectedOrders(): void {
    if (!this.fulfillService.checkAbilityToFulfill()) return;
    if (this.isPickingMode) {
      this.onPickingSelectedOrders();
    } else {
      const orderIds: string[] = this.selectedOrders
        .flatMap((o) => (o.fulfillmentStatus != TranslatedFulfillmentStatus.FULFILLED ? o.id : ''))
        .filter(Boolean);
      const dialogRef = this.dialog.open(MultipleFulfillmentDialog, {
        data: {
          orders: this.selectedOrders,
        },
        panelClass: 'multiple-fulfillment-dialog',
        backdropClass: 'global_modal_overlay',
      });
      dialogRef.componentInstance.onFulfill?.subscribe((results) => {
        this.fulfillService.onLoading.next(true);
        this.fulfillService
          .fulfillOrders(orderIds)
          .pipe(
            finalize(() => {
              this.fulfillService.onLoading.next(false);
              dialogRef.close();
            }),
          )
          .subscribe((fulfilledOrders) => {
            fulfilledOrders?.forEach((order) =>
              this.fulfillService.updateOrderInBatch(this.batch.id, {
                id: order.id,
                fulfillmentStatus: order.fulfillmentStatus,
              }),
            );
          });

        dialogRef.afterClosed().subscribe(() => {
          this.selectedOrders = [];
        });
      });
    }
  }

  setAbleToFulfillBulk() {
    const ableToFulfill = this.selectedOrders?.some(
      (order) => order.fulfillmentStatus !== TranslatedFulfillmentStatus.FULFILLED,
    );
    this.groupActions = [GroupAction.PRINT_FULFILL, GroupAction.RETURN_ORDERS];
    if (ableToFulfill) {
      this.groupActions.unshift(GroupAction.FULFILL_ORDERS);
    }
  }

  onBulkPrint(action) {
    const ids = this.selectedOrders.map((order) => order.id);
    this._printService.printOrder(action, ids);
    this.selectedOrders = [];
  }

  onChangeSelectedOrders(selectedOrders: Order[]): void {
    this.selectedOrders = selectedOrders;
    this.setAbleToFulfillBulk();
  }

  async onActiveRow(order: Order) {
    this.router.navigate(['.'], { relativeTo: this.route, queryParams: { from: 'batch' } });
    try {
      this.fulfillService.setPanelState(PanelState.ORDER);
      const expandedOrder = await this.fulfillService.getOrder(order.id);
      this.fulfillService.setActiveOrder(expandedOrder);
      const prevOrder = getAdjacentRow(expandedOrder, this.filteredOrders, AdjacentRowDirection.PREVIOUS);
      const nextOrder = getAdjacentRow(expandedOrder, this.filteredOrders, AdjacentRowDirection.NEXT);
      [prevOrder, nextOrder].filter(Boolean).forEach((order) => {
        this.fulfillService.preloadOrderForBrowsing(order.id);
      });
    } catch (e) {
      this.fulfillService.setPanelState(PanelState.BATCH);
    }
  }

  updateOrderToDeliveredStatus(row: Order): void {
    const state: string = 'DELIVERED';
    const fontSize = '18';
    const preText: string = 'You are about to mark order';
    const postText: string = 'as DELIVERED';
    const displayOrderNumbersOnly = true;
    const dialogRef = this.dialog.open(HolddialogComponent, {
      maxWidth: '100vw',
      width: '28vw',
      panelClass: 'hold_popup',
      backdropClass: 'global_modal_overlay',
      disableClose: true,
      data: { rows: [row], state, preText, postText, displayOrderNumbersOnly, fontSize },
    });
    dialogRef.componentInstance.actionResponse?.subscribe(
      (result: { deliveryTransaction: DeliveryTransaction; id: string }) => {
        const orders = [...this.orders];
        const updatedOrder = orders.find((order) => (order.id = result.id));
        if (updatedOrder) {
          updatedOrder.deliveryTransaction = { ...updatedOrder.deliveryTransaction, ...result.deliveryTransaction };
          this.setOrders(orders);
        }
      },
    );
  }

  changeColorCoding({ order, colors, selectedOrders }) {
    const orderIds: string[] = selectedOrders ? this.selectedOrders.map((order) => order.id) : [order.id];
    const previousRows = this.orders;
    this.assignColorsCodingUpdate(orderIds.map((id) => ({ id, colorCoding: colors })));
    this.orderService.updateOrdersColorCoding(orderIds, colors).subscribe({
      next: (succededOrders) => {
        if (succededOrders && succededOrders?.length < orderIds.length) {
          const rowsToRevert = previousRows.filter(
            (row) => !succededOrders.some((succededOrder) => succededOrder.id === row.id),
          );
          this.assignColorsCodingUpdate(rowsToRevert);
        }
      },
      error: (error) => {
        this.assignColorsCodingUpdate(previousRows);
        this.toastService.showToast({
          message: `Failed to update color coding`,
          mode: 'danger',
        });
      },
    });
  }

  private assignColorsCodingUpdate(updatedOrders: Pick<Order, 'id' | 'colorCoding'>[]) {
    const newRows = assignColorCodingToRows(this.orders, updatedOrders, this.colorCodingData);
    this.setOrders(newRows);
  }

  createQueryParameters(sortFields: SortField[] = [new SortField('createdDate', 'DESC')]): QueryParameters {
    const queryParameters = new QueryParameters();
    queryParameters.anyAll = 'all';
    queryParameters.filters = [
      new Filter('orderStatus', 'NOT IN', ['hold', 'scheduled', 'stepAssign', 'stepNew']),
      new Filter('deleted', 'IN', ['0']),
    ];
    queryParameters.stepFilter = createStepFilter(OrderStatus.STEP_PRINT);
    queryParameters.sortFields = sortFields;
    return queryParameters;
  }

  getBatchOrders(): void {
    const queryParameters = this.createQueryParameters();

    if (this.tableFilters?.length) {
      queryParameters.filters = [...queryParameters.filters, ...this.tableFilters];
    }

    this.orderService.getOrdersObs(queryParameters).subscribe((data: { orders: Order[]; count: number }) => {
      this.processOrders(this.batch, data.orders);
    });
  }

  processOrders(batch: any, orders: any): void {
    if (!orders) {
      return;
    }
    orders = orders.filter((o) => this.filteredOrders.find((fo) => fo.id === o.id));
    const ordersFulfillmentProcess: OrderInBulkFulfillmentProcess[] = BatchBulkFulfillmentProcessMap.get(batch.id);

    orders?.forEach((order) => {
      if (ordersFulfillmentProcess?.length) {
        const targetOrder = ordersFulfillmentProcess.find((o) => o.id === order.id);
        order.inProcess = targetOrder?.fulfillmentProcess === 'running';
      }
      order.deliveryPromise = getDeliveryPromise(order);
      order.formattedShippingCityAndProvince = formatShippingAddressString(order);
      order.foramttedShippingName = formatShippingNameString(order);
      if (order?.deliveryTransaction) {
        order.selectedCarrierName = this.loadCarriersOptionsAndCarrierName(batch, order);
      }
      // Show tracking number instead of shipment id:
      const carrierTypesList = [
        CarrierTypeNames.EASYSHIP,
        CarrierTypeNames.EASYSHIP_TPL,
        CarrierTypeNames.EASYSHIP_MA_INTERNATIONAL,
        CarrierTypeNames.DOORDASH,
        CarrierTypeNames.DOORDASH_MA,
        CarrierTypeNames.DOORDASH_DASHLINK,
      ];
      if (
        carrierTypesList.includes(this.batch.selectedCarrier.type.name) &&
        order.deliveryTransaction?.trackingNumber
      ) {
        order.deliveryTransaction.shippingId = order.deliveryTransaction.trackingNumber;
      }

      if (order?.deliveryTransaction?.shippingPriceAmount && order?.deliveryTransaction?.shippingPriceCurrency) {
        // add currency sign to rate price
        const { shippingPriceAmount, shippingPriceCurrency } = order?.deliveryTransaction;
        const price =
          typeof shippingPriceAmount === 'number'
            ? shippingPriceAmount
            : // extract float number:
              shippingPriceAmount.replace(/[^\d\.]*/g, '');
        const convertedPrice = this.getCurrencyConvertedPrice(price, shippingPriceCurrency);
        const currency = this.isMACarrier ? this.shopsCurency : shippingPriceCurrency;
        const shippingPriceAmountWithCurrency = addCurrencySignToText(convertedPrice, currency);
        order.deliveryTransaction.shippingPriceAmount = shippingPriceAmountWithCurrency;

        // add currency sign to shipment fee
        const currencySign = getSignOfCurrency(currency);
        const deliveryPrice = parseFloat(order.deliveryMethodPrice);
        order.orderShipmentFee = isNumber(deliveryPrice) ? `${currencySign}${deliveryPrice.toFixed?.(2)}` : null;

        // add currency sign to insurance fee
        if (isNumber(order?.deliveryTransaction?.metadata?.insurance?.insuranceFee)) {
          const { insuranceFee } = order.deliveryTransaction.metadata.insurance;
          order.deliveryTransaction.metadata.insurance.insuranceFee = `${currencySign}${insuranceFee.toFixed?.(2)}`;
        }
      }
      if (order.maximalShippingHours && !order.formatted) {
        order.maximalShippingHours = formattedServiceLevel(order);
        order.formatted = true;
      }
      order.orderBatch = this.batch;
      if (order?.customer) {
        order.customerFormated = formatBillTo(order, this.fulfillService.shopMetadata?.isHideEmailInBillTo);
      }

      if (this.colorCodingData) {
        order.currentColorTooltipString =
          !order.colorCoding || order.colorCoding.includes('transparent')
            ? ''
            : order.colorCoding
                .map((colorValue) => {
                  const current = this.colorCodingData?.find((colorData) => colorData.value === colorValue);
                  return current?.label || current?.defaultName || '';
                })
                .join(', ');
      }
      // does not suppoerted right now, the tables don't have orderItems data for this calculation
      // order.isPicked = this.isPickingMode && order.orderItems?.every((orderItem) => orderItem.internalStatus == InternalOrderItemStatus.PICKED);
    });
    this.loadCarriersOptions();
    // in order to update order rows we need to provide new array after processing:
    this.setOrders(orders ? [...orders] : []);
  }

  loadCarriersOptions(): void {
    const selectedCarrierColumnIndex = columnsConfigs.findIndex((item) => item.prop === 'selectedCarrierName');
    columnsConfigs[selectedCarrierColumnIndex].filterSelectOptions = this.selectedCarrierOptions;
  }

  loadCarriersOptionsAndCarrierName(orderBatch, row: Order): string {
    const carrierName = orderBatch?.selectedCarrier?.name ?? '';
    let formattedCarrierText: string = carrierName;
    let formattedCarrierValue: string = carrierName;

    if (row.deliveryTransaction?.trackingCarrierName) {
      const trackingCarrierName = row.deliveryTransaction.trackingCarrierName;
      formattedCarrierText = `${carrierName} - ${trackingCarrierName ?? ''}`;
      formattedCarrierValue = `deliveryTransaction:${trackingCarrierName}`;
    }

    const existsCarrier = this.selectedCarrierOptions.find((options) => options.text === formattedCarrierText);
    if (!existsCarrier) {
      this.selectedCarrierOptions.push({
        value: formattedCarrierValue,
        text: formattedCarrierText,
      });
    }
    this.selectedCarrierOptions.sort((a, b) => a.text.localeCompare(b.text));
    return formattedCarrierText;
  }

  onChangeTableFilter(columnIndex: number): void {
    this.tableFilters = [];
    let fieldName;
    // always init orders to be filtered inside this batch
    this.tableFilters.push(new Filter('batchName', 'LIKE', [this.batch.name]));

    this.columns.forEach((column: ColumnSettings) => {
      if (!column.filterType || !column.filterOption || (!column.filterValue && !column.filterValueArray?.length)) {
        return;
      }

      fieldName = column?.filterFields || column.prop;

      let filterValue = Array.isArray(column.filterValue) ? column.filterValue : [column.filterValue];

      if (column.filterType === TableFilterType.COLOR) {
        filterValue = column.filterValueArray;
      } else if (column.filterOption === 'DATE RANGE' || column.filterOption === 'DATE RANGE STRICT') {
        filterValue = [];
        if (column.filterValueStart) {
          filterValue.push(column.filterValueStart);
        }
        if (column.filterValueEnd) {
          const endDate = new Date(Date.parse(column.filterValueEnd));
          endDate.setDate(endDate.getDate() + 1);
          modifyDateRangeValuesByColumn(column.prop, { endDate: endDate });
          filterValue.push(endDate.toISOString());
        }
      }
      if (column.filterType !== TableFilterType.TAGS) {
        this.tableFilters.push(
          new Filter(
            fieldName,
            column.filterOption,
            (filterValue as string[]).map((x) => x.trim()),
          ),
        );
      }

      if (column.filterType === TableFilterType.TAGS && column.filterValueArray?.length > 0) {
        column.filterValueArray.forEach((filterValue) => {
          this.tableFilters.push(new Filter(filterValue.type, column.filterOption, [filterValue.name]));
        });
      }
    });
    this.getBatchOrders();
  }

  onReturnOrderToAssign(event): void {
    this.fulfillService.backToAssign(event, this.batch.id);
  }

  sortColumns(event) {
    const originalOrders = _.clone(this.orders);
    this.setOrders([]);
    const column = this.columns[event.index]?.prop;
    if (column) {
      if (event.sortDirection == 'ASC') {
        this.setOrders(_.orderBy(originalOrders, [column], ['asc']));
      } else {
        this.setOrders(_.orderBy(originalOrders, [column], ['desc']));
      }
    }
  }

  openedChange(event) {
    event.stopPropagation();
  }

  toggleColumn(): void {
    this.loadSelectedColumns();
  }

  loadSelectedColumns(): void {
    localStorage.setItem('fulfill-columns', JSON.stringify(this.selectedColumns));
    this.columns = columnsConfigs.filter(
      (column: ColumnSettings) => !column.canBeHide || this.selectedColumns.includes(column.prop),
    );
    const packageColumnIndex = this.columns.findIndex((col) => col.name === 'Package name');
    if (packageColumnIndex > 0) {
      this.loadDefaultPackagesOptions(packageColumnIndex);
    }
  }

  loadDefaultPackagesOptions(index: number): void {
    this.shopService.shopDefaultBoxes$.pipe(takeUntil(this.destroy$)).subscribe(({ activeBoxes, inactiveBoxes }) => {
      const boxes = [...activeBoxes];

      const boxOptions = [];
      let customBoxOpt;
      boxes.forEach((box) => {
        const res = {
          value: box.name.toLowerCase(),
          text: box.name,
        };
        if (box.isCustom) {
          customBoxOpt = res;
        } else {
          boxOptions.push(res);
        }
      });
      if (customBoxOpt) boxOptions.push(customBoxOpt);
      this.columns[index].filterSelectOptions = boxOptions;
    });
  }

  loadLocalStorageDefaultColumns(): void {
    const data = localStorage.getItem('fulfill-columns');
    this.selectedColumns = data
      ? JSON.parse(data)
      : this.COLUMN_OPTIONS.filter((column: ColumnSettings) => !column.canBeHide || column.default).map(
          (column: ColumnSettings) => column.prop,
        );
    this.loadSelectedColumns();
  }

  setOrders(orders) {
    this.orders = orders;
  }
}
